import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link2, Mail, Building2, RefreshCw, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

interface ConnectedSource {
  id: string;
  provider: string;
  status: string;
  lastSyncAt: string | null;
  accountEmail?: string;
}

interface ConnectedSourcesCardProps {
  sources: ConnectedSource[];
  onSync?: (provider: string) => void;
  onDisconnect?: (provider: string) => void;
  syncing?: string | null;
}

const providerConfig: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  gmail: {
    label: "Gmail",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
    ),
    color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  },
  powens: {
    label: "Powens (Banques)",
    icon: <Building2 className="w-5 h-5 text-blue-600" />,
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  },
  dropbox: {
    label: "Dropbox",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#0061FF" d="M12 6.17L6 2 0 6.17l6 4.16 6-4.16zm-6 8.33l6-4.17 6 4.17-6 4.16-6-4.16zM0 10.33l6 4.17v4.17l-6-4.17v-4.17zm18-4.16l-6 4.16 6 4.17 6-4.17-6-4.16zm-6 8.33v4.17l6 4.16v-4.16l-6-4.17z"/>
      </svg>
    ),
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  },
};

export function ConnectedSourcesCard({ sources, onSync, onDisconnect, syncing }: ConnectedSourcesCardProps) {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "Jamais";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (sources.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary" />
            <CardTitle className="text-base">Sources connectées</CardTitle>
          </div>
          <CardDescription>Aucune source connectée pour le moment</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Connectez Gmail, Dropbox ou vos banques pour importer automatiquement vos documents.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-primary" />
          <CardTitle className="text-base">Sources connectées</CardTitle>
        </div>
        <CardDescription>{sources.length} source(s) active(s)</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {sources.map((source) => {
          const config = providerConfig[source.provider] || {
            label: source.provider,
            icon: <Mail className="w-5 h-5" />,
            color: "bg-secondary text-secondary-foreground",
          };
          const isSyncing = syncing === source.provider;

          return (
            <div
              key={source.id}
              className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/50"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color}`}>
                  {config.icon}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">{config.label}</p>
                    {source.status === "active" ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Dernière sync : {formatDate(source.lastSyncAt)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {onSync && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onSync(source.provider)}
                    disabled={isSyncing}
                  >
                    {isSyncing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
